import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import { Menu, MenuItem, Theme, Tooltip, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import {
  selectAgentMode,
  selectIsAgentModeEngaged,
} from '../../redux/features/agentMode/agentMode.selector';
import { setAgentMode } from '../../redux/features/agentMode/agentMode.slice';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';

/**
 * The width below which the three labelled segments no longer fit beside
 * everything else the header must always show.
 *
 * From the header's own budget at 0.75rem: ~849px for today's bar, ~1094px with
 * the expanded switch, ~949px with the pill. (Both switch figures grew ~32px
 * when it was sized up to match the 32px avatar and the restaurant Button, which
 * is why this threshold moved from 1080.) Nothing else in the bar ever truncates
 * — the switch is the only element that gives way.
 */
const COLLAPSE_BELOW_PX = 1120;

/**
 * HITL-1647: two states, not three. `Supervise` is retired — it had no
 * capability of its own, and made the agent choose before the console would let
 * them do anything.
 */
const MODES: AgentMode[] = [AgentMode.shadow, AgentMode.intercept];

const useStyles = makeStyles((theme: Theme) => ({
  track: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    height: 32,
    padding: 3,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.20)',
    // Never shrinks: the header's slack pools in classes.title, which is where
    // this sits, so it takes its width from there rather than from a neighbour.
    flexShrink: 0,
  },
  // Every segment carries the restaurant picker's label typography — 0.75rem
  // at weight 500, uppercase, MUI Button letter-spacing — so the switch reads as
  // the same class of control as "CHOOSE ONE" beside it. Uniform across all
  // three, which also means the track's width never changes as the mode does.
  // The uppercasing is presentational only; `AgentMode`'s values are unchanged,
  // and the dropdown shows them in their natural case.
  segment: {
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1.75),
    borderRadius: 13,
    border: 0,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.02857em',
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.74)',
    '&:hover:not(:disabled)': {
      backgroundColor: 'rgba(255,255,255,0.12)',
      color: Colors.white,
    },
    '&:disabled': {
      opacity: 0.3,
      cursor: 'not-allowed',
    },
  },
  cell: {
    gridArea: '1 / 1',
  },
  /**
   * An invisible copy of a mode name, stacked in the same grid cell as the
   * visible one. Used only by the collapsed pill, which shows a single mode: a
   * hidden copy of all three keeps it as wide as the longest name, so changing
   * mode cannot shunt the indicator row sideways. The expanded segments need no
   * such trick — their typography is uniform, so their widths already are.
   */
  sizer: {
    gridArea: '1 / 1',
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  // Selection is carried by the amber fill alone — one active colour for all
  // three, so "selected" reads as one kind of state rather than three. Amber
  // background with black text is the pair the restaurant picker and the avatar
  // already use, so every selected control in this bar looks the same.
  //
  // Armed Intercept deliberately looks identical to engaged Intercept: an
  // outlined variant would be a third visual idiom in a bar that has one. The
  // armed/engaged difference stays legible from its neighbours instead — while
  // merely armed all three segments are still selectable, and once engaged the
  // other two are visibly disabled — plus the Pulse band, which turns red only
  // once the agent has actually acted.
  segmentOn: {
    backgroundColor: Colors.amber,
    color: Colors.primaryButtonTextColor,
    '&:hover:not(:disabled)': {
      backgroundColor: Colors.amber,
      color: Colors.primaryButtonTextColor,
    },
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 26,
    padding: theme.spacing(0, 1.5),
    borderRadius: 13,
    border: 0,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    // Always shows the selected value, so it always carries the selected
    // typography — the picker's label style.
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.02857em',
    whiteSpace: 'nowrap',
    backgroundColor: Colors.amber,
    color: Colors.primaryButtonTextColor,
  },
  // The label area is a grid holding the visible mode plus a hidden copy of
  // every mode name, so the pill is as wide as the longest one and does not
  // resize as the mode changes. Renaming a mode cannot break this.
  pillLabel: {
    display: 'grid',
    placeItems: 'center start',
  },
  pillIcon: {
    fontSize: '1rem !important',
    opacity: 0.75,
  },
  menuLabel: {
    padding: theme.spacing(0.75, 2, 0.25),
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
}));

/**
 * HITL-1604 — the supervising agent's mode, in the header.
 *
 * Pressing a segment **changes nothing but permission**: it unlocks the agent's
 * surfaces, while the wire events that act on the customer's behalf stay where
 * they already are, on the agent's real actions. Acting here would fail
 * dangerous — an accidental press would silence the AI and leave nobody serving
 * the customer — so `Intercept` stays reversible until the agent actually acts,
 * and becomes one-way only then. The single thing a press does put on the wire
 * is HITL-1605's `agent_mode_change` metric, which controls nothing; see
 * `select()` below.
 */
const AgentModeSwitch: FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const isEnabled = useAppSelector(selectIsShadowModeEnabled);
  const mode = useAppSelector(selectAgentMode);
  const engaged = useAppSelector(selectIsAgentModeEngaged);
  const isCollapsed = useMediaQuery(`(max-width:${COLLAPSE_BELOW_PX - 1}px)`);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!isEnabled) return null;

  // Once the agent has acted in Intercept the AI has been told to stop, so there
  // is nothing to go back to.
  const isLocked = engaged && mode === AgentMode.intercept;
  const isArmed = mode === AgentMode.intercept && !engaged;

  /**
   * HITL-1605 — the transition goes on the wire from here, at the press.
   *
   * Deliberately not a `useEffect` watching `selectAgentMode`: an effect fires
   * on remount, fires on the session-start resets in the slice (which must emit
   * nothing — every session begins in `Shadow` by contract), and would report
   * transitions the reducer refused, since `setAgentMode` returns early once
   * engaged in `Intercept`. At the click both `from` and `to` are in hand and
   * only a real, accepted, agent-initiated change reaches the dispatch.
   *
   * It emits the metric and **nothing else** — no `agent_interception`, no
   * `agent_first_interception`, no `order`, no TTS. That asymmetry is the point:
   * emitting an interception on the press would fire on an *accidental* press
   * too, silencing the AI with nobody serving the customer. The interception
   * still follows later, from the agent's first real action, at that action's own
   * call site. The metric controls nothing; it is observability only.
   */
  const select = (next: AgentMode) => {
    setAnchorEl(null);
    if (isLocked) return;
    // Re-pressing the current mode is not a transition.
    if (next === mode) return;
    dispatch(setAgentMode(next));
    // After the mode change, so the metric can only ever record a transition
    // that actually happened.
    dispatch(
      messagingActions.sendAgentMetric({
        data: { metric: 'agent_mode_change', from: mode, to: next },
      })
    );
  };

  if (isCollapsed) {
    return (
      <>
        <div
          className={classes.track}
          role="group"
          aria-label="Agent mode"
          data-testid="agent-mode-switch"
          data-collapsed="true"
          data-mode={mode}
          data-engaged={engaged}
        >
          <Tooltip
            title={
              isLocked
                ? 'Intercept cannot be left for the rest of this session'
                : 'Agent mode'
            }
            placement="bottom"
          >
            <button
              type="button"
              className={classes.pill}
              data-testid="agent-mode-pill"
              aria-haspopup="true"
              onClick={(event) =>
                isLocked ? undefined : setAnchorEl(event.currentTarget)
              }
            >
              <span className={classes.pillLabel}>
                {/* One hidden copy per mode, all in the same cell, so the pill
                    takes the width of the longest name and never resizes as the
                    mode changes. */}
                {MODES.map((value) => (
                  <span
                    key={value}
                    aria-hidden="true"
                    className={classes.sizer}
                  >
                    {value}
                  </span>
                ))}
                <span
                  className={classes.cell}
                  data-testid="agent-mode-pill-label"
                >
                  {mode}
                </span>
              </span>
              {isLocked ? (
                <LockIcon className={classes.pillIcon} />
              ) : (
                <ArrowDropDownIcon className={classes.pillIcon} />
              )}
            </button>
          </Tooltip>
        </div>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // Renders in a portal outside the header, so mark it the way
          // ProfileMenu does — the AI auto-mode intercept listener must ignore
          // clicks on its items.
          data-profile-menu="true"
        >
          <Typography key="mode-label" className={classes.menuLabel}>
            Agent mode
          </Typography>
          {/* Plain rows, matching the task-router lobby's availability Select
              (TaskRouterHeaderInfo): MUI's own `selected` highlight is the
              indicator, so there is no separate dot or tick to keep in sync. */}
          {MODES.map((value) => (
            <MenuItem
              key={value}
              selected={mode === value}
              disabled={isLocked && value !== mode}
              data-testid={`agent-mode-option-${value}`}
              onClick={() => select(value)}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <div
      className={classes.track}
      role="group"
      aria-label="Agent mode"
      data-testid="agent-mode-switch"
      data-collapsed="false"
      data-mode={mode}
      data-engaged={engaged}
    >
      {MODES.map((value) => {
        const isOn = mode === value;
        const armedHere = isOn && isArmed;
        return (
          <button
            key={value}
            type="button"
            className={`${classes.segment}${
              isOn ? ` ${classes.segmentOn}` : ''
            }`}
            aria-pressed={isOn}
            data-testid={`agent-mode-segment-${value}`}
            data-armed={armedHere}
            // Disabled, not hidden: the agent should still see the modes they
            // can no longer return to.
            disabled={isLocked && !isOn}
            onClick={() => select(value)}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
};

export default AgentModeSwitch;
